import { useCallback, useRef } from 'react';
import { ALE_CONFIG } from '../config';
import { aleClient } from '../lib/aleClient';

const { sessionStorageKey: KEY, sessionFallbackTtlMs: FALLBACK_TTL } = ALE_CONFIG;

function readSession() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (raw && raw.token && raw.exp && Date.now() < raw.exp) {
      return raw;
    }
  } catch (_) {
    /* ignore */
  }
  localStorage.removeItem(KEY);
  return null;
}

function writeSession(token, exp) {
  const session = { token, exp: exp || Date.now() + FALLBACK_TTL };
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch (_) {
    /* localStorage indisponível: segue em memória */
  }
  return session;
}

// Gerencia o token de sessão do Ale no navegador, com validade (~7 dias).
// O token NÃO é a chave da IA — só autoriza conversar com o proxy do backend.
export function useAleSession() {
  // Evita criar duas sessões em paralelo.
  const pendingRef = useRef(null);

  const ensureSession = useCallback(async (forceNew = false) => {
    if (!forceNew) {
      const existing = readSession();
      if (existing) return existing.token;
    }

    if (pendingRef.current) return pendingRef.current;

    pendingRef.current = (async () => {
      try {
        const { data } = await aleClient.createSession();
        const token = data?.token;
        if (!token) throw new Error('Sessão do Ale sem token');
        writeSession(token, data?.exp);
        return token;
      } finally {
        pendingRef.current = null;
      }
    })();

    return pendingRef.current;
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(KEY);
  }, []);

  return { ensureSession, clearSession };
}
